import React, { useCallback, useEffect, useMemo, useState } from "react";

/* ===================== STATS & GRAPH COMPONENTS ===================== */
import StatsBox from "../StatsBox/StatsBox";
import APITrendChart from "../APITrendChart/APITrendChart";
import PieChart from "../../Charts/PieChart";

/* ===================== COMMON COMPONENTS ===================== */
import Input from "../../Input/Input";
import APITable from "../../Tables/APITable";
import Pagination from "../../Pagination/Pagination";
import DateRangePicker from "../../Calender/DateRangePicker";
import Button from "../../Button/Button";
import Modal from "../../Modal/Modal";
import ToggleSwitch from "../../ToggleSwitch/ToggleSwitch";
import Notification from "../../Notification/Notification";
import {
  StatsSkeleton,
  TableSkeleton,
  TrendSkeleton,
} from "../../Skeleton/StatsSkeleton";

/* ===================== ICONS ===================== */
import {
  AlertTriangle,
  Building2,
  Calendar,
  Clock,
  Download,
  PackageOpen,
  PieChart as PieChartIcon,
  TrendingUp,
  Wallet,
  Zap,
} from "lucide-react";

/* ===================== SERVICES ===================== */
import { apiGet, apiPatch } from "../../../apiService";
import { getErrorMessage } from "../../../utils/errorHelper";
import { can } from "../../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "../../../pages/Admin/AdminProductUsage/AdminProductUsage.module.scss";

const formatCurrency = (value) => {
  const numberValue = Number(value || 0);
  return `₹${numberValue.toLocaleString("en-IN")}`;
};

const ProductUsagePanel = () => {
  const userType = localStorage.getItem("userType");
  const isInternalUser = userType === "Internal";

  /* ===================== DASHBOARD DATA ===================== */
  const [statsData, setStatsData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [topErrors, setTopErrors] = useState([]);
  const [topOrgs, setTopOrgs] = useState([]);
  const [productPie, setProductPie] = useState([]);
  const [windowInfo, setWindowInfo] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);

  /* ===================== TABLE DATA ===================== */
  const [rows, setRows] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);

  /* ===================== NOTIFICATIONS ===================== */
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");
  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* ===================== MARK DUPLICATE (INTERNAL ONLY) ===================== */
  const [dupModal, setDupModal] = useState(null); // { row, target }
  const [dupReason, setDupReason] = useState("");
  const [dupSaving, setDupSaving] = useState(false);

  /* ===================== PAGINATION ===================== */
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
  });

  /* ===================== SEARCH & FILTER ===================== */
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [datePickerKey, setDatePickerKey] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedOrg, setSelectedOrg] = useState("");
  const [dateRange, setDateRange] = useState("");
  // Transaction type: "" (all) | "1" (Consume) | "2" (Added)
  const [selectedType, setSelectedType] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [products, setProducts] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  const organizationMap = useMemo(() => {
    return organizations.reduce((map, org) => {
      map[org.id] = org.companyName;
      return map;
    }, {});
  }, [organizations]);

  const selectedProductCode =
    products.find((product) => String(product.id) === selectedProduct)?.code ||
    "";

  const resetDateFilter = () => {
    setFromDate("");
    setToDate("");
    setPage(1);
    setDatePickerKey((prev) => prev + 1);
  };

  const handleErrorClick = (row) => {
    setShowErrorModal(true);
    setErrorDetails(row.error_response);
  };

  const buildQuery = useCallback(
    (includePaging = true) => {
      return new URLSearchParams({
        ...(includePaging && { page, limit }),
        ...(fromDate && toDate && { from_date: fromDate, to_date: toDate }),
        ...(dateRange && { dateRange, date_range: dateRange }),
        ...(appliedSearch && { search: appliedSearch }),
        ...(selectedProduct && {
          product_id: selectedProduct,
          productId: selectedProduct,
          ...(selectedProductCode && { product_code: selectedProductCode }),
        }),
        ...(selectedType && { type: selectedType }),
        ...(isInternalUser &&
          selectedOrg && {
            orgId: selectedOrg,
            org_id: selectedOrg,
            organization_id: selectedOrg,
          }),
      }).toString();
    },
    [
      appliedSearch,
      dateRange,
      fromDate,
      isInternalUser,
      limit,
      page,
      selectedOrg,
      selectedProduct,
      selectedProductCode,
      selectedType,
      toDate,
    ],
  );

  const fetchDropdowns = useCallback(async () => {
    try {
      const productRes = await apiGet(
        "catalog",
        "/apimaster/products/dropdown",
      );
      if (productRes?.status === "success") {
        setProducts(productRes.data || []);
      }
    } catch (err) {
      console.error("Product dropdown API error", err);
    }

    if (!isInternalUser) return;

    try {
      const orgRes = await apiGet("user", "/organization/dropdown");
      if (orgRes?.status === "success") {
        setOrganizations(orgRes.data || []);
      }
    } catch (err) {
      console.error("Organization dropdown API error", err);
    }
  }, [isInternalUser]);

  const buildDashboardQuery = useCallback(() => {
    return new URLSearchParams({
      ...(fromDate && toDate && { from_date: fromDate, to_date: toDate }),
      ...(dateRange && { dateRange, date_range: dateRange }),
      ...(appliedSearch && { search: appliedSearch }),
      ...(selectedProduct && {
        product_id: selectedProduct,
        productId: selectedProduct,
        ...(selectedProductCode && { product_code: selectedProductCode }),
      }),
      ...(isInternalUser &&
        selectedOrg && {
          orgId: selectedOrg,
          org_id: selectedOrg,
          organization_id: selectedOrg,
        }),
    }).toString();
  }, [
    appliedSearch,
    dateRange,
    fromDate,
    isInternalUser,
    selectedOrg,
    selectedProduct,
    selectedProductCode,
    toDate,
  ]);

  const fetchDashboardData = useCallback(async () => {
    setDashLoading(true);
    setTrendLoading(true);
    try {
      const query = buildDashboardQuery();
      const endpoint = query
        ? `/product-usage/dashboard?${query}`
        : "/product-usage/dashboard";
      const res = await apiGet("catalog", endpoint);

      if (res?.status === "success") {
        const summary = res.data?.summary || {};

        setStatsData([
          {
            title: "Total Product Calls",
            value: summary.totalCalls ?? 0,
            type: "blue",
            icon: <Zap size={24} />,
            subText: `${summary.activeOrgs ?? 0} active orgs`,
          },
          {
            title: "Success Rate",
            value: `${summary.successRate ?? 0}%`,
            type: "green",
            icon: <TrendingUp size={24} />,
            subText: `${summary.errorRate ?? 0}% error rate`,
          },
          {
            title: "Average Response",
            value: `${summary.avgResponseMs ?? 0} ms`,
            type: "orange",
            icon: <Clock size={24} />,
            subText: `P95 ${summary.p95ResponseMs ?? 0} ms`,
          },
          isInternalUser
            ? {
                title: "Profit",
                value: formatCurrency(summary.profit),
                type: Number(summary.profit || 0) < 0 ? "red" : "green",
                icon: <Wallet size={24} />,
                subText: `Margin ${summary.profitMarginPct ?? 0}% · Revenue ${formatCurrency(summary.revenue)} · Cost ${formatCurrency(summary.internalCost)}`,
              }
            : {
                title: "Amount Spend",
                value: formatCurrency(summary.amountSpent),
                type: "blue",
                icon: <Wallet size={24} />,
                subText: `${summary.creditsConsumed ?? 0} credits consumed`,
              },
        ]);

        setTrendData(
          (res.data?.trend || []).map((item) => ({
            day: item.day,
            success: item.success_calls || 0,
            failed: item.failed_calls || 0,
          })),
        );

        setTopProducts(res.data?.topProducts || []);
        setTopErrors(res.data?.topErrors || []);
        setTopOrgs(res.data?.topOrgs || []);
        setProductPie(res.data?.productPie || []);
        setWindowInfo(res.data?.window || null);
      }
    } catch (err) {
      console.error("Product usage dashboard API error", err);
    } finally {
      setDashLoading(false);
      setTrendLoading(false);
    }
  }, [buildDashboardQuery, isInternalUser]);

  const fetchTableData = useCallback(async () => {
    setLogsLoading(true);

    try {
      const query = buildQuery(true);
      const res = await apiGet("catalog", `/product-logs?${query}`);

      if (res?.success) {
        setRows(res.data || []);
        setPagination({
          total: res.pagination?.total || 0,
          totalPages: res.pagination?.totalPages || 1,
        });
      }
    } catch (err) {
      console.error("Product logs API error", err);
    } finally {
      setLogsLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  const handleDateChange = useCallback((type, value) => {
    if (type === "from") {
      setFromDate((currentValue) => {
        if (currentValue === value) return currentValue;
        if (value) setDateRange("");
        return value;
      });
    }
    if (type === "to") {
      setToDate((currentValue) => {
        if (currentValue === value) return currentValue;
        if (value) setDateRange("");
        return value;
      });
    }
  }, []);

  useEffect(() => {
    setPage(1);
  }, [fromDate, toDate]);

  /* ===================== DUPLICATE HANDLERS ===================== */
  const openDuplicateModal = (row) => {
    setDupModal({ row, target: !row.is_duplicate });
    setDupReason("");
  };

  const confirmMarkDuplicate = async () => {
    if (!dupModal) return;
    const { row, target } = dupModal;
    const txnId = row.id ?? row.transaction_id ?? row.transactionId;

    // Reason is required when flagging a transaction as duplicate.
    if (target && !dupReason.trim()) {
      showNotification("Please enter a reason", "error");
      return;
    }

    setDupSaving(true);
    try {
      const res = await apiPatch(
        "catalog",
        `/transactions/${txnId}/mark-duplicate`,
        { isDuplicate: target, reason: dupReason.trim() },
      );

      if (res?.status === "success") {
        showNotification(
          res?.message ||
            (target ? "Transaction marked as duplicate" : "Duplicate removed"),
          "success",
        );
        setRows((prev) =>
          prev.map((r) =>
            (r.id ?? r.transaction_id ?? r.transactionId) === txnId
              ? { ...r, is_duplicate: target }
              : r,
          ),
        );
        setDupModal(null);
        setDupReason("");
      } else {
        showNotification(res?.message || "Something went wrong", "error");
      }
    } catch (err) {
      showNotification("Failed to update duplicate status", "error");
    } finally {
      setDupSaving(false);
    }
  };

  const columns = [
    { label: "Name", key: "product_name" },
    { label: "Created At", key: "created_at" },
    { label: "Code", key: "code" },
    { label: "Transaction No", key: "transaction_no" },
    { label: "User Name", key: "user_name" },
    { label: "State", key: "state" },
    { label: "Branch", key: "branch" },
    { label: "LOS ID", key: "los_id" },
    { label: "Status", key: "status" },
    { label: "Duplicate", key: "is_duplicate" },
    { label: "Error Code / Success Code", key: "error_code" },
    {
      // The API keeps the credit_points_consumed key even for type=2 (Added)
      // rows, so only the label switches with the filter.
      label:
        selectedType === "2" ? "Credit Points Added" : "Credit Points Consumed",
      key: "credit_points_consumed",
    },
    { label: "Response Time", key: "response_time_ms" },
    { label: "Err Response", key: "error_response" },
    ...(isInternalUser
      ? [
          { label: "Organization Name", key: "organization_name" },
          { label: "Profit", key: "profit" },
          { label: "Internal cost", key: "internal_cost" },
        ]
      : []),
  ];

  /* Server-side export: the backend returns the full filtered dataset
     (no paging) when download=true is passed. If it streams a file we save
     it as-is; if it responds with the JSON envelope we convert the rows to
     properly-escaped CSV here. */
  const csvEscape = (value) => {
    if (value === null || value === undefined) return "";
    const str =
      typeof value === "object" ? JSON.stringify(value) : String(value);
    return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const rowsToCsv = (dataRows) => {
    const header = columns.map((c) => csvEscape(c.label)).join(",");
    const body = dataRows
      .map((row) =>
        columns
          .map((column) => {
            if (column.key === "organization_name") {
              return csvEscape(
                row.organization_name ||
                  row.companyName ||
                  row.company_name ||
                  organizationMap[row.orgId || row.org_id] ||
                  "",
              );
            }
            return csvEscape(row[column.key]);
          })
          .join(","),
      )
      .join("\r\n");
    return `${header}\r\n${body}`;
  };

  const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const query = buildQuery(false);
      const endpoint = `/product-logs?${query ? `${query}&` : ""}download=true`;
      const response = await apiGet("catalog", endpoint, { rawResponse: true });

      const disposition = response.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || "product_usage_logs.csv";

      const contentType = response.headers.get("Content-Type") || "";

      if (contentType.includes("application/json")) {
        // JSON envelope → build the CSV from the returned rows.
        const payload = await response.json();
        const dataRows = payload?.data || [];
        if (!dataRows.length) {
          showNotification("No records to download", "error");
          return;
        }
        // BOM so Excel reads UTF-8 (₹, accents) correctly.
        const blob = new Blob(["\uFEFF" + rowsToCsv(dataRows)], {
          type: "text/csv;charset=utf-8;",
        });
        saveBlob(blob, filename);
      } else {
        // Real file stream (CSV/XLSX) → save as-is.
        saveBlob(await response.blob(), filename);
      }
    } catch (err) {
      console.error("Product logs download error", err);
      showNotification(
        getErrorMessage(err, "Failed to download product logs"),
        "error",
      );
    } finally {
      setDownloading(false);
    }
  };

  const piePropData = productPie.map((item) => ({
    category: item.name,
    hit_percentage: item.hitPercentage,
  }));

  return (
    <>
      {windowInfo && (
        <div className={styles["admin-api-usage__window-chip"]}>
          <Calendar size={14} />
          <span>
            {windowInfo.from} → {windowInfo.to}
          </span>
          {windowInfo.dateRange && (
            <span className={styles["admin-api-usage__window-chip-range"]}>
              {windowInfo.dateRange.replace(/_/g, " ")}
            </span>
          )}
        </div>
      )}

      <div className={styles["admin-api-usage__stats-grid"]}>
        {dashLoading
          ? [...Array(4)].map((_, i) => <StatsSkeleton key={i} />)
          : statsData.map((item, i) => <StatsBox key={i} {...item} />)}
      </div>

      {trendLoading ? (
        <TrendSkeleton columns={2} rows={1} />
      ) : (
        <APITrendChart data={trendData} />
      )}

      {(productPie.length > 0 || topProducts.length > 0) && (
        <div className={styles["admin-api-usage__insights-grid"]}>
          {productPie.length > 0 && (
            <div className={styles["admin-api-usage__insight-card"]}>
              <div className={styles["admin-api-usage__insight-head"]}>
                <h3 className={styles["admin-api-usage__insight-title"]}>
                  <PieChartIcon size={18} />
                  Product Distribution
                </h3>
                <p className={styles["admin-api-usage__insight-subtitle"]}>
                  Share of calls by product
                </p>
              </div>
              <PieChart pieGraphData={piePropData} />
            </div>
          )}

          {topProducts.length > 0 && (
            <div className={styles["admin-api-usage__insight-card"]}>
              <div className={styles["admin-api-usage__insight-head"]}>
                <h3 className={styles["admin-api-usage__insight-title"]}>
                  <PackageOpen size={18} />
                  Top Products
                </h3>
                <p className={styles["admin-api-usage__insight-subtitle"]}>
                  Most-called products in this window
                </p>
              </div>
              <ul className={styles["admin-api-usage__rank-list"]}>
                {topProducts.map((product, idx) => (
                  <li
                    key={product.id ?? idx}
                    className={styles["admin-api-usage__rank-row"]}
                  >
                    <span className={styles["admin-api-usage__rank-badge"]}>
                      #{idx + 1}
                    </span>
                    <div className={styles["admin-api-usage__rank-main"]}>
                      <span className={styles["admin-api-usage__rank-name"]}>
                        {product.name}
                      </span>
                      <span className={styles["admin-api-usage__rank-meta"]}>
                        {product.code}
                      </span>
                    </div>
                    <div className={styles["admin-api-usage__rank-stats"]}>
                      <span>{product.calls} calls</span>
                      {isInternalUser ? (
                        <>
                          <span>{formatCurrency(product.revenue)} rev</span>
                          <span
                            className={
                              Number(product.profit) < 0
                                ? styles["admin-api-usage__rank-loss"]
                                : styles["admin-api-usage__rank-gain"]
                            }
                          >
                            {formatCurrency(product.profit)} profit
                          </span>
                        </>
                      ) : (
                        <span>
                          {formatCurrency(product.amountSpent)} spent
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {(topErrors.length > 0 || (isInternalUser && topOrgs.length > 0)) && (
        <div className={styles["admin-api-usage__insights-grid"]}>
          {topErrors.length > 0 && (
            <div className={styles["admin-api-usage__insight-card"]}>
              <div className={styles["admin-api-usage__insight-head"]}>
                <h3 className={styles["admin-api-usage__insight-title"]}>
                  <AlertTriangle size={18} />
                  Top Errors
                </h3>
                <p className={styles["admin-api-usage__insight-subtitle"]}>
                  Most frequent error codes
                </p>
              </div>
              <ul className={styles["admin-api-usage__rank-list"]}>
                {topErrors.map((err, idx) => (
                  <li
                    key={`${err.errorCode}-${err.productName}-${idx}`}
                    className={styles["admin-api-usage__rank-row"]}
                  >
                    <span
                      className={`${styles["admin-api-usage__rank-badge"]} ${styles["admin-api-usage__rank-badge--error"]}`}
                    >
                      {err.errorCode}
                    </span>
                    <div className={styles["admin-api-usage__rank-main"]}>
                      <span className={styles["admin-api-usage__rank-name"]}>
                        {err.productName}
                      </span>
                    </div>
                    <div className={styles["admin-api-usage__rank-stats"]}>
                      <span>
                        {err.occurrences}{" "}
                        {err.occurrences === 1 ? "occurrence" : "occurrences"}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isInternalUser && topOrgs.length > 0 && (
            <div className={styles["admin-api-usage__insight-card"]}>
              <div className={styles["admin-api-usage__insight-head"]}>
                <h3 className={styles["admin-api-usage__insight-title"]}>
                  <Building2 size={18} />
                  Top Organizations
                </h3>
                <p className={styles["admin-api-usage__insight-subtitle"]}>
                  Top orgs by calls in this window
                </p>
              </div>
              <ul className={styles["admin-api-usage__rank-list"]}>
                {topOrgs.map((org, idx) => {
                  const orgName =
                    organizationMap[org.orgId] ||
                    organizationMap[org.org_id] ||
                    org.companyName ||
                    "Unknown Organization";
                  return (
                    <li
                      key={`${org.orgId}-${idx}`}
                      className={styles["admin-api-usage__rank-row"]}
                    >
                      <span className={styles["admin-api-usage__rank-badge"]}>
                        #{idx + 1}
                      </span>
                      <div className={styles["admin-api-usage__rank-main"]}>
                        <span className={styles["admin-api-usage__rank-name"]}>
                          {orgName}
                        </span>
                      </div>
                      <div className={styles["admin-api-usage__rank-stats"]}>
                        <span>{org.calls} calls</span>
                        <span>{formatCurrency(org.revenue)} rev</span>
                        <span
                          className={
                            Number(org.profit) < 0
                              ? styles["admin-api-usage__rank-loss"]
                              : styles["admin-api-usage__rank-gain"]
                          }
                        >
                          {formatCurrency(org.profit)} profit
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className={styles["admin-api-usage__card"]}>
        <div className={styles["admin-api-usage__controls-row"]}>
          <div className={styles["admin-api-usage__tabs"]}>
            <button
              className={`${styles["admin-api-usage__tab"]} ${styles["admin-api-usage__tab--active"]}`}
              type="button"
              onClick={() => {
                resetDateFilter();
                setAppliedSearch("");
                setSearch("");
                setSelectedProduct("");
                setSelectedOrg("");
                setDateRange("");
                setSelectedType("");
              }}
            >
              Product Logs
            </button>
          </div>

          <div className={styles["admin-api-usage__search-box"]}>
            <Input
              type="search"
              placeholder="Search product, code, transaction"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setAppliedSearch(search);
                  setPage(1);
                }
              }}
            />
            <Button
              text="Search"
              className={styles["admin-api-usage__search-btn"]}
              onClick={() => {
                setAppliedSearch(search);
                setPage(1);
              }}
            />
          </div>
        </div>

        <div className={styles["admin-api-usage__table-actions"]}>
          <div className={styles["admin-api-usage__showing-text"]}>
            Showing {rows.length} of {pagination.total}
          </div>

          <div className={styles["admin-api-usage__filters"]}>
            <select
              className={styles["admin-api-usage__select"]}
              value={selectedProduct}
              onChange={(e) => {
                setSelectedProduct(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Products</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>

            <select
              className={styles["admin-api-usage__select"]}
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Types</option>
              <option value="1">Consume</option>
              <option value="2">Added</option>
            </select>

            <select
              className={styles["admin-api-usage__select"]}
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value);
                setFromDate("");
                setToDate("");
                setDatePickerKey((prev) => prev + 1);
                setPage(1);
              }}
            >
              <option value="">All Dates</option>
              <option value="this_week">This Week</option>
              <option value="last_week">Last Week</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
            </select>

            {isInternalUser && (
              <select
                className={styles["admin-api-usage__select"]}
                value={selectedOrg}
                onChange={(e) => {
                  setSelectedOrg(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Organisations</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.companyName}
                  </option>
                ))}
              </select>
            )}

            <DateRangePicker
              key={datePickerKey}
              maxDate={new Date()}
              onChange={handleDateChange}
            />

            {can("product-usage", "export") && (
              <button
                className={styles["admin-api-usage__download-btn"]}
                onClick={handleDownload}
                disabled={downloading}
              >
                <Download size={18} />
                {downloading ? "Downloading..." : "Download"}
              </button>
            )}
          </div>
        </div>

        {logsLoading ? (
          <TableSkeleton columns={columns.length} rows={5} />
        ) : (
          <APITable
            columns={columns}
            data={rows}
            renderCell={(row, key) => {
              if (["user_name", "state", "branch", "los_id"].includes(key)) {
                return row[key] || "--";
              }

              if (key === "created_at") {
                const datetime = row.created_at;
                if (!datetime) return "--";
                const [date, time, meridian] = datetime.split(" ");
                return (
                  <span>
                    {date}{" "}
                    <span className={styles["admin-api-usage__time-color"]}>
                      {time} {meridian}
                    </span>
                  </span>
                );
              }

              if (key === "status") {
                const isSuccess = row.status === "Success";
                const isHold = row.status === "Hold";
                return (
                  <span
                    className={`${styles["admin-api-usage__status-badge"]} ${
                      isSuccess
                        ? styles["admin-api-usage__status-badge--success"]
                        : isHold
                          ? styles["admin-api-usage__status-badge--warning"]
                          : styles["admin-api-usage__status-badge--error"]
                    }`}
                  >
                    {row.status || "--"}
                  </span>
                );
              }

              if (key === "error_code") {
                const codeNum = parseInt(row.error_code, 10);
                const isSuccess =
                  Number.isFinite(codeNum) && codeNum >= 200 && codeNum < 300;
                return (
                  <span
                    className={`${styles["admin-api-usage__error-link"]} ${
                      isSuccess
                        ? styles["admin-api-usage__error-link--success"]
                        : ""
                    }`}
                    onClick={() => handleErrorClick(row)}
                  >
                    {row.error_code || "--"}
                  </span>
                );
              }

              if (key === "error_response") {
                if (!row.error_response) return "--";
                return (
                  <span
                    className={styles["admin-api-usage__error-link"]}
                    onClick={() => handleErrorClick(row)}
                  >
                    View
                  </span>
                );
              }

              if (key === "organization_name") {
                return (
                  row.organization_name ||
                  row.companyName ||
                  row.company_name ||
                  organizationMap[row.orgId || row.org_id] ||
                  "--"
                );
              }

              if (key === "response_time_ms") {
                return row.response_time_ms
                  ? `${row.response_time_ms} ms`
                  : "--";
              }

              if (key === "profit" || key === "internal_cost") {
                return row[key] === null || row[key] === undefined
                  ? "--"
                  : formatCurrency(row[key]);
              }

              if (key === "is_duplicate") {
                // Internal (superadmin) → toggle to mark/unmark duplicate.
                if (
                  isInternalUser &&
                  can("product-usage", "mark-duplicate-transaction")
                ) {
                  return (
                    <div className={styles["admin-api-usage__dup-cell"]}>
                      <ToggleSwitch
                        isOn={!!row.is_duplicate}
                        onToggle={() => openDuplicateModal(row)}
                      />
                      {row.is_duplicate && (
                        <span
                          className={`${styles["admin-api-usage__status-badge"]} ${styles["admin-api-usage__status-badge--duplicate"]}`}
                        >
                          Duplicate
                        </span>
                      )}
                    </div>
                  );
                }
                // Client (org admin) → read-only duplicate indicator.
                return row.is_duplicate ? (
                  <span
                    className={`${styles["admin-api-usage__status-badge"]} ${styles["admin-api-usage__status-badge--duplicate"]}`}
                  >
                    Duplicate
                  </span>
                ) : (
                  "--"
                );
              }

              return null;
            }}
            rowClassName={(row) =>
              row.is_duplicate ? styles["admin-api-usage__dup-row"] : ""
            }
          />
        )}

        {showErrorModal && (
          <Modal
            title="Response"
            onClose={() => setShowErrorModal(false)}
            className="error_details_modal"
          >
            <pre className={styles["admin-api-usage__json-block"]}>
              {(() => {
                if (!errorDetails) return "--";

                if (typeof errorDetails === "string") {
                  try {
                    const parsed = JSON.parse(errorDetails);
                    return JSON.stringify(parsed, null, 2);
                  } catch {
                    return errorDetails;
                  }
                }

                return JSON.stringify(errorDetails, null, 2);
              })()}
            </pre>
          </Modal>
        )}

        <Pagination
          currentPage={page}
          totalPages={pagination.totalPages}
          rowsPerPage={limit}
          onPageChange={setPage}
          onRowsPerPageChange={(nextLimit) => {
            setLimit(nextLimit);
            setPage(1);
          }}
        />
      </div>

      {/* TOAST */}
      {notification && (
        <div className={styles["admin-api-usage__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* MARK DUPLICATE CONFIRM MODAL */}
      {dupModal && (
        <Modal
          title={dupModal.target ? "Mark as Duplicate" : "Remove Duplicate"}
          onClose={() => {
            if (dupSaving) return;
            setDupModal(null);
            setDupReason("");
          }}
        >
          <div className={styles["admin-api-usage__dup-modal"]}>
            <p className={styles["admin-api-usage__dup-modal-text"]}>
              {dupModal.target ? (
                <>
                  Mark transaction{" "}
                  <strong>{dupModal.row?.transaction_no}</strong> as a duplicate?
                </>
              ) : (
                <>
                  Remove the duplicate flag from{" "}
                  <strong>{dupModal.row?.transaction_no}</strong>?
                </>
              )}
            </p>

            <Input
              type="textarea"
              label={dupModal.target ? "Reason" : "Reason (optional)"}
              placeholder="e.g. duplicate of TXN-2026-1043 — stuck job retried, both committed"
              style={{ height: "110px" }}
              value={dupReason}
              onChange={(e) => setDupReason(e.target.value)}
              required={dupModal.target}
            />

            <div className={styles["admin-api-usage__dup-modal-actions"]}>
              <Button
                text={
                  dupSaving
                    ? "Saving..."
                    : dupModal.target
                      ? "Mark as Duplicate"
                      : "Remove Duplicate"
                }
                disabled={dupSaving}
                onClick={confirmMarkDuplicate}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ProductUsagePanel;
